import { FC } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import FileCheckboxList from '../FileCheckboxList';
import { toggleExclusivePath } from '../ProcessReportModal.utils';
import { useProcessReportModalContext } from '../ProcessReportModalContext';
import WorkboardDropboxPicker from '../WorkboardDropboxPicker';

const ProcessReportInputsPanel: FC = () => {
  const {
    companyName,
    useS3Fallback,
    initialPickerPrefix,
    selectedModelPath,
    selectedReportPath,
    setSelectedModelPath,
    setSelectedReportPath,
    modelFolderPath,
    reportFolderPath,
    modelCheckboxFiles,
    reportCheckboxFiles,
    isLoadingModelFiles,
    isLoadingReportFiles,
    inputsSummary,
    processButtonLabel,
    canProcess,
    runProcess,
    isPipelineBusy,
    isStartingExtraction,
    isExtractionProcessing,
    isDrafting,
    isExtractionStatusError,
    isExtractionFailed,
    pipelineStatusColor,
    pipelineStatusMessage,
    latestExtractionRun,
    dashboardOpinionUrl,
  } = useProcessReportModalContext();

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {inputsSummary}
      </Typography>
      <Button
        variant="contained"
        onClick={() => runProcess()}
        disabled={!canProcess}
        id="draft-final-report-email-button"
      >
        {processButtonLabel}
      </Button>
      {(isPipelineBusy || isExtractionStatusError || isExtractionFailed) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: pipelineStatusColor,
          }}
        >
          {(isExtractionProcessing || isDrafting || isStartingExtraction) && (
            <CircularProgress size={16} />
          )}
          <Typography variant="caption">{pipelineStatusMessage}</Typography>
        </Box>
      )}
      {latestExtractionRun?.run_id && !isPipelineBusy && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}
          data-testid="latest-extraction-run-banner"
        >
          <Typography variant="body2" color="text.secondary">
            Last extraction
            {latestExtractionRun.created_at
              ? ` (${new Date(latestExtractionRun.created_at).toLocaleString()})`
              : ''}
            : {latestExtractionRun.status ?? 'unknown'}
          </Typography>
          {dashboardOpinionUrl ? (
            <Button
              component="a"
              href={dashboardOpinionUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: 'none' }}
              id="view-dashboard-opinion-link"
              data-testid="view-dashboard-opinion-link"
            >
              View on dashboard
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Dashboard link will appear after extraction completes and data is imported.
            </Typography>
          )}
        </Box>
      )}

      {useS3Fallback ? (
        <Box>
          <Typography variant="subtitle2">Valuation files</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            No valuation folder linked — browse a valuation directory in the backup bucket.
          </Typography>
          <WorkboardDropboxPicker
            key={initialPickerPrefix || 'root'}
            companyName={companyName}
            initialPrefix={initialPickerPrefix}
            selectedModelKey={selectedModelPath}
            selectedReportKey={selectedReportPath}
            onSelectModel={setSelectedModelPath}
            onSelectReport={setSelectedReportPath}
          />
        </Box>
      ) : (
        <>
          <Box>
            <Typography variant="subtitle2">Model Filename</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
              {modelFolderPath}
            </Typography>
            <FileCheckboxList
              files={modelCheckboxFiles}
              selectedPath={selectedModelPath}
              onSelect={path => setSelectedModelPath(prev => toggleExclusivePath(prev, path))}
              isLoading={isLoadingModelFiles}
              emptyLabel="No files found in the Model folder."
              idPrefix="model-file"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2">Final Report File Name</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
              {reportFolderPath}
            </Typography>
            <FileCheckboxList
              files={reportCheckboxFiles}
              selectedPath={selectedReportPath}
              onSelect={path => setSelectedReportPath(prev => toggleExclusivePath(prev, path))}
              isLoading={isLoadingReportFiles}
              emptyLabel="No files found in the Report folder."
              idPrefix="report-file"
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ProcessReportInputsPanel;
